/**
 * Project Selector Component
 * Dropdown to display and switch between projects
 */

import { Fragment } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Menu, Transition } from '@headlessui/react';
import { ChevronDownIcon, FolderIcon } from '@heroicons/react/24/outline';
import { projectService } from '@/services/projectService';
import { useProjectStore } from '@/store/projectStore';

export function ProjectSelector() {
  const { currentProject, setCurrentProject } = useProjectStore();

  const { data: projects = [] } = useQuery({
    queryKey: ['projects'],
    queryFn: () => projectService.getProjects(),
  });

  if (!currentProject && projects.length === 0) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 text-sm text-gray-500">
        <FolderIcon className="h-5 w-5" />
        <span>No projects</span>
      </div>
    );
  }

  return (
    <Menu as="div" className="relative inline-block text-left">
      <Menu.Button className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2">
        <FolderIcon className="h-5 w-5 text-gray-400" />
        <span className="max-w-[150px] truncate">
          {currentProject?.name || 'Select Project'}
        </span>
        {currentProject && (
          <span className="px-1.5 py-0.5 text-xs font-semibold text-primary-700 bg-primary-100 rounded">
            {currentProject.key}
          </span>
        )}
        <ChevronDownIcon className="h-4 w-4 text-gray-400" />
      </Menu.Button>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute left-0 z-10 mt-2 w-72 origin-top-left rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
          <div className="py-1 max-h-96 overflow-y-auto">
            {projects.length === 0 ? (
              <div className="px-4 py-3 text-sm text-gray-500">
                No projects available
              </div>
            ) : (
              projects.map((project) => (
                <Menu.Item key={project.id}>
                  {({ active }) => (
                    <button
                      onClick={() => {
                        setCurrentProject(project);
                      }}
                      className={`${
                        active ? 'bg-gray-100' : ''
                      } ${
                        currentProject?.id === project.id
                          ? 'bg-primary-50 text-primary-700'
                          : 'text-gray-900'
                      } group flex w-full items-center justify-between px-4 py-2 text-sm`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <FolderIcon className="h-5 w-5 flex-shrink-0 text-gray-400" />
                        <div className="flex flex-col items-start min-w-0">
                          <span className="font-medium truncate max-w-full">
                            {project.name}
                          </span>
                          {project.description && (
                            <span className="text-xs text-gray-500 truncate max-w-full">
                              {project.description}
                            </span>
                          )}
                        </div>
                      </div>
                      <span className="ml-2 flex-shrink-0 px-1.5 py-0.5 text-xs font-semibold text-primary-700 bg-primary-100 rounded">
                        {project.key}
                      </span>
                    </button>
                  )}
                </Menu.Item>
              ))
            )}
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  );
}

export default ProjectSelector;
